import * as React from 'react'

import { Heading } from '../../../components'
import { ChipSwitch, ChipSwitchButton } from '../../../components/ChipSwitch'
import { RedGreenText } from '../../../components/RedGreenText'
import { Table, TableAlign, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table'
import { FORMAT_PERCENT, formatNumber, shortFormatDollar, shortFormatNumber } from '../../../util'
import { ChangeTypeEnum, HoldingOptionTypeLabelMap, ManagerData, TopInvestmentType } from '../constants'
import IndividualManagersSection from './IndividualManagersSection'
import { TickerButton } from './TickerButton'

enum TopInvestmentTypeEnum {
  Buys = 'buys',
  Sells = 'sells',
}

const TopInvestmentLabels = {
  [TopInvestmentTypeEnum.Buys]: 'Top Buys',
  [TopInvestmentTypeEnum.Sells]: 'Top Sells',
}

interface Props
  extends Pick<ManagerData, 'latestSummary' | 'topBuysUsd' | 'topBuysPct' | 'topSellsUsd' | 'topSellsPct'> {
  individualManagers?: ManagerData['individualManagers']
}

export function DetailsSummary({
  latestSummary,
  individualManagers,
  topSellsPct,
  topSellsUsd,
  topBuysUsd,
  topBuysPct,
}: Props) {
  const [topBuysChange, setTopBuysChange] = React.useState(ChangeTypeEnum.Usd)
  const [topSellsChange, setTopSellsChange] = React.useState(ChangeTypeEnum.Usd)

  const generalStatistics = React.useMemo(
    () => [
      ['Market Value - This Q', shortFormatDollar(latestSummary.totalSumOfUsdValue)],
      ['Market Value - Prev Q', shortFormatDollar(latestSummary.totalSumOfUsdValuePrevQ)],
      ['Number of Holdings', shortFormatNumber(latestSummary.totalCountOfInvestments, { fractions: 0 })],
      ['New Purchases', shortFormatNumber(latestSummary.countOfNewPurchased, { fractions: 0 })],
      ['Added To', shortFormatNumber(latestSummary.countOfAddedTo, { fractions: 0 })],
      ['Reduced', shortFormatNumber(latestSummary.countOfReduced, { fractions: 0 })],
      ['Top 10 Holdings %', formatNumber(latestSummary.percOfPortfolioOfTop10, FORMAT_PERCENT)],
      ['Time Held Top 10', `${shortFormatNumber(latestSummary.timeHeldTop10)} Q`],
      ['Time Held Top 20', `${shortFormatNumber(latestSummary.timeHeldTop20)} Q`],
      ['Time Held All', `${shortFormatNumber(latestSummary.timeHeldAll)} Q`],
    ],
    [latestSummary]
  )

  return (
    <div className="relative">
      <div className="absolute bottom-3 left-[50%] top-3 -translate-x-2/4 border-l border-secondary"></div>

      <div className="h-full rounded-lg border border-primary p-2.5">
        <div className="grid grid-cols-2 gap-8">
          {[TopInvestmentTypeEnum.Buys, TopInvestmentTypeEnum.Sells].map((topInvestmentType) => {
            const isBuys = topInvestmentType === TopInvestmentTypeEnum.Buys
            const isSells = topInvestmentType === TopInvestmentTypeEnum.Sells
            const isPercentage =
              (isBuys && topBuysChange === ChangeTypeEnum.Percentage) ||
              (isSells && topSellsChange === ChangeTypeEnum.Percentage)
            const topTradeChange = topInvestmentType === TopInvestmentTypeEnum.Buys ? topBuysChange : topSellsChange
            const setTopTradeChange =
              topInvestmentType === TopInvestmentTypeEnum.Buys ? setTopBuysChange : setTopSellsChange
            let topTrades: TopInvestmentType[] = []
            if (isBuys) {
              topTrades = isPercentage ? topBuysPct : topBuysUsd
            } else if (isSells) {
              topTrades = isPercentage ? topSellsPct : topSellsUsd
            }

            return (
              <div key={topInvestmentType}>
                <div className="mb-2 flex w-full items-center justify-between px-1.5">
                  <Heading level={5}>{TopInvestmentLabels[topInvestmentType]}</Heading>
                  <ChipSwitch size="small">
                    <ChipSwitchButton
                      active={topTradeChange === ChangeTypeEnum.Usd}
                      onClick={() => {
                        setTopTradeChange(ChangeTypeEnum.Usd)
                      }}
                    >
                      <span className="xl:hidden">$ Chng.</span>
                      <span className="hidden xl:inline">$ Change</span>
                    </ChipSwitchButton>
                    <ChipSwitchButton
                      active={topTradeChange === ChangeTypeEnum.Percentage}
                      onClick={() => {
                        setTopTradeChange(ChangeTypeEnum.Percentage)
                      }}
                    >
                      <span className="xl:hidden">% Chng.</span>
                      <span className="hidden xl:inline">% Change</span>
                    </ChipSwitchButton>
                  </ChipSwitch>
                </div>
                <Table className="styled-table-new is-condensed">
                  <TableHead>
                    <TableHeader align={TableAlign.Left} className="pl-0">
                      Ticker
                    </TableHeader>
                    <TableHeader align={TableAlign.Left} width="40">
                      Type
                    </TableHeader>
                    <TableHeader align={TableAlign.Right} width="95">
                      Change
                    </TableHeader>
                  </TableHead>
                  <TableBody>
                    {topTrades.map(({ ticker, name, value, putCall, boxoverData }, index) => (
                      <TableRow key={`${ticker}-${index}`}>
                        <TableCell className="pl-0">
                          <TickerButton
                            className="font-normal"
                            ticker={ticker}
                            name={name}
                            putCall={putCall}
                            boxoverData={boxoverData}
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-2">{HoldingOptionTypeLabelMap[putCall]}</span>
                        </TableCell>
                        <TableCell align={TableAlign.Right}>
                          <RedGreenText value={value} rightContent={null}>
                            {Number.isFinite(value) && isPercentage
                              ? formatNumber(value, FORMAT_PERCENT)
                              : shortFormatNumber(value)}
                          </RedGreenText>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          })}
        </div>
        <div className="my-4 border-t border-secondary"></div>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="mb-2 px-1.5">
              <Heading level={5}>General Statistics</Heading>
            </div>
            <Table className="styled-table-new whitespace-nowrap">
              <TableBody>
                {generalStatistics.map(([ticker, change], index) => (
                  <TableRow key={index}>
                    <TableCell className="pl-0">{ticker}</TableCell>
                    <TableCell>{change}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <IndividualManagersSection individualManagers={individualManagers} />
        </div>
      </div>
    </div>
  )
}
